import { useId, useState, type MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import type { SxProps, Theme } from "@mui/material/styles";
import { IOSSwitch } from "@/components/editable/EditableField/fields/SwitchField";

export interface BookingsSetting {
  checked: boolean;
  onChange: (checked: boolean) => void;
}

interface BookingsSettingsMenuProps {
  liveUpdates: BookingsSetting;
}

const menuPaperSx: SxProps<Theme> = {
  mt: 0.5,
  minWidth: 240,
  borderRadius: "8px",
  border: "1px solid #e4e4e7",
  boxShadow: "0 4px 6px -1px rgb(0 0 0 / 10%), 0 2px 4px -2px rgb(0 0 0 / 10%)",
  "& .MuiList-root": { p: 0.5 },
};

const menuItemSx: SxProps<Theme> = {
  gap: 3,
  minHeight: 44,
  px: 1.25,
  borderRadius: "4px",
  justifyContent: "space-between",
};

const pausedBadgeSx: SxProps<Theme> = {
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  padding: "4px 10px",
  borderRadius: "999px",
  background: "#fff3d6",
  color: "#7a4f00",
  fontSize: 12,
  fontWeight: 600,
  whiteSpace: "nowrap",
  marginInlineEnd: "8px",
  "&::before": {
    content: '""',
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    background: "#d99400",
  },
};

export const BookingsSettingsMenu = ({
  liveUpdates,
}: BookingsSettingsMenuProps) => {
  const { t } = useTranslation();
  const menuId = useId();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const open = anchor != null;
  const liveUpdatesPaused = !liveUpdates.checked;

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchor(event.currentTarget);
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flexShrink: 0,
        marginInlineStart: "auto",
      }}
    >
      <Box
        component="span"
        sx={liveUpdatesPaused ? pausedBadgeSx : undefined}
        role="status"
      >
        {liveUpdatesPaused ? t("bookings.settings.liveUpdatesPaused") : ""}
      </Box>
      <IconButton
        disableRipple
        sx={{
          width: 44,
          height: 44,
          flexShrink: 0,
          "&.Mui-focusVisible": {
            outline: "2px solid",
            outlineColor: "primary.main",
            outlineOffset: 2,
            transition: "none",
          },
          "@media (prefers-reduced-motion: no-preference)": {
            transition: "scale 120ms cubic-bezier(0.2, 0, 0, 1)",
            "&:active:not(.Mui-focusVisible)": { scale: 0.97 },
          },
        }}
        aria-label={t("bookings.settings.label")}
        aria-haspopup="menu"
        aria-controls={open ? menuId : undefined}
        aria-expanded={open ? "true" : undefined}
        onClick={handleOpen}
      >
        <SettingsOutlinedIcon />
      </IconButton>
      <Menu
        id={menuId}
        anchorEl={anchor}
        open={open}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: menuPaperSx } }}
      >
        <MenuItem
          disableRipple
          role="menuitemcheckbox"
          aria-checked={liveUpdates.checked}
          aria-labelledby={`${menuId}-live-updates`}
          aria-describedby={`${menuId}-live-updates-description`}
          sx={menuItemSx}
          onClick={() => liveUpdates.onChange(!liveUpdates.checked)}
        >
          <div>
            <div id={`${menuId}-live-updates`}>
              {t("bookings.settings.liveUpdates")}
            </div>
            <Box
              id={`${menuId}-live-updates-description`}
              sx={{ mt: 0.25, fontSize: 12, color: "text.secondary" }}
            >
              {t("bookings.settings.liveUpdatesDescription")}
            </Box>
          </div>
          {/* This is just decorative, the menu item is the control. */}
          <IOSSwitch
            checked={liveUpdates.checked}
            tabIndex={-1}
            sx={{
              pointerEvents: "none",
              "& .MuiSwitch-switchBase": {
                transition:
                  "transform 150ms cubic-bezier(0.25, 0.46, 0.45, 0.94), color 150ms ease",
                "@media (prefers-reduced-motion: reduce)": {
                  transition: "color 150ms ease",
                },
              },
              "& .MuiSwitch-track": {
                transition: "background-color 150ms ease, opacity 150ms ease",
              },
            }}
            slotProps={{ input: { "aria-hidden": true, readOnly: true } }}
          />
        </MenuItem>
      </Menu>
    </Box>
  );
};
